import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency, formatPercent } from "@/lib/utils";
import {
  Plane, DollarSign, TrendingUp, Megaphone, Users, Wrench,
  Route, Shield, Sparkles, Calendar, Fuel, MapPin, AlertTriangle
} from "lucide-react";

// V2 + V1 decision field definitions, grouped by section
const DECISION_SECTIONS = [
  {
    title: "Pricing & Fares",
    icon: DollarSign,
    fields: [
      { key: "fare_strategy", label: "Fare Strategy" },
      { key: "discount_level", label: "Discount Level", suffix: "%" },
      { key: "target_segment", label: "Target Segment" },
      { key: "ancillary_package", label: "Ancillary Package" },
      { key: "fare_per_mile", label: "Fare / Mile", format: "currency2" },
      { key: "cabin_level", label: "Cabin Level" },
    ]
  },
  {
    title: "Marketing & Sales",
    icon: Megaphone,
    fields: [
      { key: "marketing_budget", label: "Marketing Budget", format: "currency" },
      { key: "loyalty_program", label: "Loyalty Program" },
      { key: "promotional_campaign", label: "Promotional Campaign" },
      { key: "promotion_budget", label: "Promotion Budget", format: "currency" },
      { key: "advertising_budget", label: "Advertising Budget", format: "currency" },
      { key: "sales_force_count", label: "Sales Force" },
    ]
  },
  {
    title: "Fleet",
    icon: Plane,
    fields: [
      { key: "aircraft_purchases", label: "Aircraft Purchases" },
      { key: "aircraft_leases", label: "Aircraft Leases" },
      { key: "aircraft_disposals", label: "Aircraft Disposals" },
    ]
  },
  {
    title: "Human Resources",
    icon: Users,
    fields: [
      { key: "salary_level", label: "Salary Level" },
      { key: "training_investment", label: "Training Investment", format: "currency" },
      { key: "benefits_package", label: "Benefits Package" },
      { key: "pilots_hire", label: "Pilots Hired" },
      { key: "cabin_crew_hire", label: "Cabin Crew Hired" },
      { key: "ground_staff_hire", label: "Ground Staff Hired" },
      { key: "training_budget", label: "Training Budget", format: "currency" },
      { key: "compensation_policy", label: "Compensation Policy" },
      { key: "bonus_plan", label: "Bonus Plan" },
    ]
  },
  {
    title: "Operations & Maintenance",
    icon: Wrench,
    fields: [
      { key: "maintenance_spending", label: "Maintenance Spending", format: "currency" },
      { key: "maintenance_strategy", label: "Maintenance Strategy" },
      { key: "ontime_target", label: "On-Time Target" },
      { key: "fuel_hedging", label: "Fuel Hedging", suffix: "%" },
      { key: "ground_handling", label: "Ground Handling" },
      { key: "catering_level", label: "Catering Level" },
      { key: "fuel_contract", label: "Fuel Contract" },
      { key: "cargo_service", label: "Cargo Service" },
    ]
  },
  {
    title: "Financing",
    icon: TrendingUp,
    fields: [
      { key: "short_term_borrowing", label: "Short-Term Borrowing", format: "currency" },
      { key: "long_term_borrowing", label: "Long-Term Borrowing", format: "currency" },
      { key: "debt_repayment", label: "Debt Repayment", format: "currency" },
      { key: "share_issuance", label: "Shares Issued" },
      { key: "share_buyback", label: "Share Buyback" },
      { key: "dividend_per_share", label: "Dividend / Share", format: "currency2" },
      { key: "short_term_loan", label: "Short-Term Loan", format: "currency" },
      { key: "long_term_loan", label: "Long-Term Loan", format: "currency" },
      { key: "stock_sale", label: "Stock Sale" },
      { key: "cd_purchase", label: "CD Purchase", format: "currency" },
    ]
  },
  {
    title: "Strategic",
    icon: Sparkles,
    fields: [
      { key: "strategic_decision", label: "Strategic Decision" },
    ]
  },
  {
    title: "Customer Experience",
    icon: Users,
    fields: [
      { key: "inflight_entertainment", label: "In-Flight Entertainment" },
      { key: "seat_comfort", label: "Seat Comfort" },
      { key: "checkin_experience", label: "Check-In Experience" },
      { key: "baggage_handling", label: "Baggage Handling" },
      { key: "customer_service_team", label: "Customer Service" },
    ]
  },
  {
    title: "Network & Scheduling",
    icon: Calendar,
    fields: [
      { key: "schedule_optimization", label: "Schedule Optimization" },
      { key: "aircraft_utilization_target", label: "Utilization Target", suffix: "h/day" },
      { key: "turnaround_time", label: "Turnaround Time" },
      { key: "slot_timing", label: "Slot Timing" },
    ]
  },
  {
    title: "Risk & ESG",
    icon: Shield,
    fields: [
      { key: "insurance_level", label: "Insurance Level" },
      { key: "crisis_reserve", label: "Crisis Reserve", format: "currency" },
      { key: "environmental_compliance", label: "Environmental Compliance" },
      { key: "carbon_offset", label: "Carbon Offset" },
      { key: "strike_contingency", label: "Strike Contingency" },
    ]
  },
];

// Skip these internal/meta fields when showing decisions
const SKIP_KEYS = new Set([
  "_id", "id", "team_id", "game_id", "quarter", "created_at",
  "submitted", "submitted_at",
]);

// Keys already covered by DECISION_SECTIONS
const SECTION_KEYS = new Set(DECISION_SECTIONS.flatMap(s => s.fields.map(f => f.key)));

function formatValue(value, format, suffix) {
  if (value === null || value === undefined || value === "") return "—";
  if (format === "currency") return formatCurrency(value);
  if (format === "currency2") return `$${Number(value).toFixed(2)}`;
  if (format === "percent") return formatPercent(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return suffix ? `${value}${suffix}` : value.toLocaleString();
  if (typeof value === "string") return value.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  return String(value);
}

function isNonEmpty(v) {
  if (v === null || v === undefined || v === "" || v === "none" || v === "None") return false;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "boolean") return true;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v).length > 0;
  return true;
}

function labelFromKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// Renders complex values (dicts, arrays)
function ComplexValue({ value }) {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-slate-400">—</span>;
    return (
      <div className="space-y-0.5">
        {value.map((item, i) => (
          <div key={i} className="text-xs font-mono text-slate-700">
            {typeof item === "object" ? JSON.stringify(item) : String(item)}
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === "object" && value !== null) {
    return (
      <div className="space-y-0.5">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex justify-between text-xs gap-2">
            <span className="text-slate-500">{labelFromKey(k)}</span>
            <span className="font-mono text-slate-700 shrink-0">
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return <span className="font-mono text-slate-800">{String(value)}</span>;
}

// Renders route fares as a compact table
function RouteFaresBlock({ fares }) {
  if (!fares || Object.keys(fares).length === 0) return null;
  return (
    <div className="col-span-2 mt-1">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 flex items-center gap-1">
        <MapPin className="w-3 h-3" /> Route Fares
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        {Object.entries(fares).map(([route, prices]) => (
          <div key={route} className="flex justify-between text-xs">
            <span className="text-slate-500">{route}</span>
            <span className="font-mono text-slate-700">
              {typeof prices === "object"
                ? Object.entries(prices).filter(([,v]) => v > 0).map(([cls, v]) => `${cls}: $${v}`).join(", ") || "—"
                : `$${prices}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Renders route config as compact info
function RouteConfigBlock({ config }) {
  if (!config || Object.keys(config).length === 0) return null;
  return (
    <div className="col-span-2 mt-1">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 flex items-center gap-1">
        <Route className="w-3 h-3" /> Route Configuration
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        {Object.entries(config).map(([route, cfg]) => (
          <div key={route} className="flex justify-between text-xs">
            <span className="text-slate-500">{route}</span>
            <span className="font-mono text-slate-700">
              {cfg.flights_per_week || cfg.flights}f/wk
              {cfg.class_config ? `, ${cfg.class_config.replace(/_/g, " ")}` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Renders staff redundancies
function StaffBlock({ data, label }) {
  if (!data || typeof data !== "object") return null;
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono text-slate-700">
        {entries.map(([k, v]) => `${labelFromKey(k)}: ${v}`).join(", ")}
      </span>
    </div>
  );
}

// Renders marketing channel mix
function ChannelMixBlock({ mix }) {
  if (!mix || typeof mix !== "object") return null;
  return (
    <div className="flex justify-between">
      <span className="text-slate-500 truncate mr-2">Channel Mix</span>
      <span className="font-mono text-slate-800 shrink-0">
        {Object.entries(mix).map(([k, v]) => `${labelFromKey(k)} ${v}%`).join(" / ")}
      </span>
    </div>
  );
}

// Expense label mapping (covers V1 + V2 keys)
const EXPENSE_LABELS = {
  fuel: "Fuel",
  labor: "Labor",
  maintenance: "Maintenance",
  marketing: "Marketing",
  customer_experience: "Customer Experience",
  depreciation: "Depreciation",
  dynamic_fare_system: "Dynamic Fare System",
  fleet_purchases: "Fleet Purchases",
  lease_costs: "Lease Costs",
  operations: "Operations",
  risk_and_insurance: "Risk & Insurance",
  route_fees: "Route Fees",
  strategic: "Strategic",
  service: "Service",
  aircraft: "Aircraft",
  airport_fees: "Airport Fees",
  overhead: "Overhead",
};

export default function QuarterDetailPanel({ teamId, quarter, isOpen }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (isOpen && !loaded) {
      setLoading(true);
      axios.get(`${API}/teams/${teamId}/quarter-detail/${quarter}`)
        .then(res => { setDetail(res.data); setLoaded(true); })
        .catch(() => { /* Quarter detail unavailable */ })
        .finally(() => setLoading(false));
    }
  }, [isOpen, loaded, teamId, quarter]);

  if (!isOpen) return null;

  const decision = detail?.decision_snapshot || detail?.decision || {};
  const financials = detail?.financials || {};
  const revenue = financials.revenue || {};
  const expenses = financials.expenses || {};
  const financing = financials.financing || {};
  const ratings = financials.ratings || {};
  const netIncome = financials.net_income ?? financials.net_profit ?? 0;

  return (
    <tr>
      <td colSpan={8} className="p-0">
        <div className="bg-slate-50 border-y border-slate-200 px-4 py-4" data-testid={`quarter-detail-${quarter}`}>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 py-4 justify-center">
              <div className="w-4 h-4 border-2 border-slate-300 border-t-sky-500 rounded-full animate-spin" />
              Loading quarter details...
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {/* Warnings */}
              <WarningsCard warnings={detail.warnings} />
              {/* Event Impacts */}
              <EventImpactsCard financials={financials} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* ========= FINANCIAL RESULTS ========= */}
                <FinancialResultsCard
                  quarter={quarter}
                  revenue={revenue}
                  expenses={expenses}
                  financing={financing}
                  financials={financials}
                  ratings={ratings}
                  netIncome={netIncome}
                />
                {/* ========= DECISIONS ========= */}
                <DecisionsCard quarter={quarter} decision={decision} />
              </div>
            </div>
          ) : (
            <p className="text-center text-slate-400 py-4">No data available for this quarter</p>
          )}
        </div>
      </td>
    </tr>
  );
}

function WarningsCard({ warnings }) {
  if (!warnings) return null;
  const range = warnings.range_incompatible || [];
  const crew = warnings.crew_shortage || [];
  if (range.length === 0 && crew.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="quarter-warnings">
      {range.map((w, i) => (
        <div key={`range-${i}`} className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
          <span>Route <strong>{w.route}</strong>: aircraft {w.removed_aircraft?.join(", ")} removed — out of range for this route.</span>
        </div>
      ))}
      {crew.map((w, i) => {
        const labelMap = { pilots: "Pilot", cabin_crew: "Cabin crew", ground_staff: "Ground staff" };
        const label = labelMap[w.type] || "Crew";
        const avail = w.available ?? w.pilots_available;
        const needed = w.needed ?? w.pilots_needed;
        return (
          <div key={`crew-${i}`} className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-800" data-testid={`crew-shortage-${w.type || "pilots"}`}>
            <Users className="w-4 h-4 shrink-0 mt-0.5 text-rose-500" />
            <span>{label} shortage: {avail} available / {needed} needed. Flight frequencies reduced to {Math.round((w.utilisation || 0) * 100)}% capacity.</span>
          </div>
        );
      })}
    </div>
  );
}


function EventImpactsCard({ financials }) {
  const impacts = financials?.event_impacts_applied;
  if (!impacts || Object.keys(impacts).length === 0) return null;

  const LABELS = {
    fuel_cost_modifier: "Fuel cost", fare_modifier: "Fares", load_factor_mod: "Load factor",
    quality_bonus: "Quality", quality_penalty: "Quality", brand_bonus: "Brand",
    brand_damage: "Brand", brand_recovery: "Brand recovery", customer_satisfaction: "Customer satisfaction",
    employee_satisfaction: "Employee satisfaction", reliability_penalty: "Reliability",
    capacity_reduction: "Capacity", cost: "Direct cost", revenue_loss: "Revenue loss",
    salary_increase: "Salary increase", hedge_cost: "Hedging cost", restructuring_cost: "Restructuring",
    penalty: "Penalty", marketing_cost: "Marketing cost", esg_score: "ESG score",
    innovation_bonus: "Innovation", security_bonus: "Security", loyalty_boost: "Loyalty",
  };

  const formatVal = (key, val) => {
    if (["fuel_cost_modifier", "fare_modifier", "salary_increase", "capacity_reduction"].includes(key))
      return `${val > 0 ? "+" : ""}${(val * 100).toFixed(0)}%`;
    if (["cost", "revenue_loss", "hedge_cost", "restructuring_cost", "penalty", "marketing_cost"].includes(key))
      return `-$${val.toLocaleString()}`;
    return `${val > 0 ? "+" : ""}${val}`;
  };

  return (
    <div className="bg-violet-50 border border-violet-200 rounded-lg px-3 py-2" data-testid="event-impacts-card">
      <p className="text-xs font-semibold text-violet-700 mb-1">Event Impacts Applied</p>
      <div className="flex flex-wrap gap-x-4 gap-y-0.5">
        {Object.entries(impacts).filter(([, v]) => typeof v === "number" && v !== 0).map(([k, v]) => (
          <span key={k} className={`text-xs font-mono ${v > 0 && !["cost","revenue_loss","penalty","hedge_cost","restructuring_cost","marketing_cost","capacity_reduction","fuel_cost_modifier","salary_increase","brand_damage","quality_penalty","reliability_penalty"].includes(k) ? "text-emerald-700" : "text-rose-700"}`}>
            {LABELS[k] || k}: {formatVal(k, v)}
          </span>
        ))}
      </div>
    </div>
  );
}


function FinancialResultsCard({ quarter, revenue, expenses, financing, financials, ratings, netIncome }) {
  const capex = financials.capex || {};
  // Dynamic expense rows: show all non-zero, non-total, excluding fleet_purchases (now CapEx)
  const expenseRows = Object.entries(expenses)
    .filter(([k, v]) => k !== "total" && k !== "fleet_purchases" && typeof v === "number" && v !== 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <Card className="border-slate-200" data-testid={`financial-results-${quarter}`}>
      <CardHeader className="py-3 px-4 border-b border-slate-100">
        <CardTitle className="text-sm flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-500" />
          Financial Results — Q{quarter}
        </CardTitle>
      </CardHeader>
      <CardContent className="py-3 px-4 space-y-2 text-sm">
        {/* Revenue */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          {revenue.passenger != null && (
            <Row label="Passenger Revenue" value={formatCurrency(revenue.passenger)} />
          )}
          {revenue.cargo > 0 && (
            <Row label="Cargo Revenue" value={formatCurrency(revenue.cargo)} />
          )}
          {revenue.interest > 0 && (
            <Row label="Interest Income" value={formatCurrency(revenue.interest)} />
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-6 border-t border-slate-100 pt-1">
          <Row label="Total Revenue" value={formatCurrency(revenue.total || 0)} bold color="text-emerald-600" />
          <Row label="Total Expenses" value={formatCurrency(expenses.total || 0)} bold color="text-rose-600" />
        </div>

        {/* Expense breakdown */}
        {expenseRows.length > 0 && (
          <div className="border-t border-slate-100 pt-2 mt-1 grid grid-cols-2 gap-x-6 gap-y-1">
            {expenseRows.map(([key, val]) => (
              <Row key={key} label={EXPENSE_LABELS[key] || labelFromKey(key)} value={formatCurrency(val)} small />
            ))}
          </div>
        )}

        {/* Financing */}
        {Object.keys(financing).length > 0 && (
          <div className="border-t border-slate-100 pt-2 mt-1 grid grid-cols-2 gap-x-6 gap-y-1">
            {financing.interest_expense > 0 && <Row label="Interest Expense" value={formatCurrency(financing.interest_expense)} small />}
            {financing.new_borrowing > 0 && <Row label="New Borrowing" value={formatCurrency(financing.new_borrowing)} small />}
            {financing.debt_repayment > 0 && <Row label="Debt Repayment" value={formatCurrency(financing.debt_repayment)} small />}
            {financing.dividends > 0 && <Row label="Dividends" value={formatCurrency(financing.dividends)} small />}
            {financing.share_proceeds > 0 && <Row label="Share Proceeds" value={formatCurrency(financing.share_proceeds)} small />}
            {financing.buyback_cost > 0 && <Row label="Buyback Cost" value={formatCurrency(financing.buyback_cost)} small />}
          </div>
        )}

        {/* CapEx (Investing) */}
        {(capex.fleet_purchases > 0 || capex.fleet_sales > 0) && (
          <div className="border-t border-slate-100 pt-2 mt-1 grid grid-cols-2 gap-x-6 gap-y-1">
            <Row label="CapEx / Investing" value="" bold small />
            {capex.fleet_purchases > 0 && <Row label="Aircraft Purchases" value={formatCurrency(capex.fleet_purchases)} small />}
            {capex.fleet_sales > 0 && <Row label="Aircraft Sales" value={formatCurrency(capex.fleet_sales)} small />}
          </div>
        )}

        {/* Net Income */}
        <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between font-bold text-base">
          <span>Net Income</span>
          <span className={`font-mono ${netIncome >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {formatCurrency(netIncome)}
          </span>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-500">
          {financials.load_factor != null && <Row label="Load Factor" value={formatPercent(financials.load_factor)} small />}
          {financials.total_passengers > 0 && <Row label="Total Passengers" value={financials.total_passengers.toLocaleString()} small />}
          {financials.total_flights > 0 && <Row label="Total Flights" value={financials.total_flights.toLocaleString()} small />}
          {financials.stock_price > 0 && <Row label="Stock Price" value={`$${financials.stock_price.toFixed(2)}`} small />}
          {financials.market_share > 0 && <Row label="Market Share" value={`${financials.market_share.toFixed(1)}%`} small />}
        </div>

        {/* Ratings */}
        {Object.keys(ratings).length > 0 && (
          <div className="border-t border-slate-100 pt-2 mt-1 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
            {ratings.quality_rating != null && <Row label="Quality Rating" value={ratings.quality_rating.toFixed(1)} small />}
            {ratings.reliability != null && <Row label="Reliability" value={ratings.reliability} small />}
            {ratings.brand_awareness != null && <Row label="Brand Awareness" value={ratings.brand_awareness} small />}
            {ratings.customer_satisfaction != null && <Row label="Customer Satisfaction" value={ratings.customer_satisfaction} small />}
            {ratings.employee_satisfaction != null && <Row label="Employee Satisfaction" value={typeof ratings.employee_satisfaction === "number" ? ratings.employee_satisfaction.toFixed(1) : ratings.employee_satisfaction} small />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DecisionsCard({ quarter, decision }) {
  // Separate known complex fields for special rendering
  const routeFares = decision.route_fares;
  const routeConfig = decision.route_config || decision.routes_config;
  const staffRedundancies = decision.staff_redundancies;
  const channelMix = decision.marketing_channel_mix;
  const fleetBuy = decision.fleet_buy;
  const fleetLease = decision.fleet_lease;
  const fleetSell = decision.fleet_sell;
  const fleetRetire = decision.fleet_retire;
  const routesOpen = decision.routes_open;
  const routesClose = decision.routes_close;
  const codeshareRoutes = decision.codeshare_routes;
  const eventResponses = decision.event_responses;

  // Complex keys handled separately
  const COMPLEX_KEYS = new Set([
    "route_fares", "route_config", "routes_config", "staff_redundancies",
    "marketing_channel_mix", "fleet_buy", "fleet_lease", "fleet_sell",
    "fleet_retire", "routes_open", "routes_close", "codeshare_routes",
    "event_responses",
  ]);

  // Find uncategorized decision keys (not in any section and not complex/skip)
  const allCoveredKeys = new Set([...SECTION_KEYS, ...COMPLEX_KEYS, ...SKIP_KEYS]);
  const uncategorized = Object.entries(decision)
    .filter(([k, v]) => !allCoveredKeys.has(k) && isNonEmpty(v));

  return (
    <Card className="border-slate-200" data-testid={`decisions-card-${quarter}`}>
      <CardHeader className="py-3 px-4 border-b border-slate-100">
        <CardTitle className="text-sm flex items-center gap-2">
          <Wrench className="w-4 h-4 text-sky-500" />
          Decisions Submitted — Q{quarter}
        </CardTitle>
      </CardHeader>
      <CardContent className="py-3 px-4 space-y-3 text-sm max-h-[500px] overflow-y-auto">
        {Object.keys(decision).length === 0 ? (
          <p className="text-slate-400 text-center py-4">No decision data available</p>
        ) : (
          <>
            {/* Structured sections */}
            {DECISION_SECTIONS.map(section => {
              const visibleFields = section.fields.filter(f =>
                isNonEmpty(decision[f.key]) && !COMPLEX_KEYS.has(f.key)
              );
              if (visibleFields.length === 0) return null;
              return (
                <div key={section.title}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1 mb-1">
                    <section.icon className="w-3 h-3" />
                    {section.title}
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {visibleFields.map(f => (
                      <div key={f.key} className="flex justify-between">
                        <span className="text-slate-500 truncate mr-2">{f.label}</span>
                        <span className="font-mono text-slate-800 shrink-0">
                          {formatValue(decision[f.key], f.format, f.suffix)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Channel Mix */}
            {isNonEmpty(channelMix) && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1 mb-1">
                  <Megaphone className="w-3 h-3" /> Channel Mix
                </p>
                <ChannelMixBlock mix={channelMix} />
              </div>
            )}

            {/* Route Fares */}
            {isNonEmpty(routeFares) && <RouteFaresBlock fares={routeFares} />}

            {/* Route Config */}
            {isNonEmpty(routeConfig) && <RouteConfigBlock config={routeConfig} />}

            {/* Fleet changes */}
            {(isNonEmpty(fleetBuy) || isNonEmpty(fleetLease) || isNonEmpty(fleetSell) || isNonEmpty(fleetRetire)) && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1 mb-1">
                  <Plane className="w-3 h-3" /> Fleet Changes
                </p>
                {isNonEmpty(fleetBuy) && <div className="text-xs text-slate-600">Buy: {fleetBuy.map(a => a.type || JSON.stringify(a)).join(", ")}</div>}
                {isNonEmpty(fleetLease) && <div className="text-xs text-slate-600">Lease: {fleetLease.map(a => a.type || JSON.stringify(a)).join(", ")}</div>}
                {isNonEmpty(fleetSell) && <div className="text-xs text-slate-600">Sell: {fleetSell.join(", ")}</div>}
                {isNonEmpty(fleetRetire) && <div className="text-xs text-slate-600">Retire: {fleetRetire.join(", ")}</div>}
              </div>
            )}

            {/* Routes opened/closed */}
            {(isNonEmpty(routesOpen) || isNonEmpty(routesClose)) && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1 mb-1">
                  <Route className="w-3 h-3" /> Route Changes
                </p>
                {isNonEmpty(routesOpen) && <div className="text-xs text-emerald-600">Opened: {routesOpen.map(r => r.route_id || JSON.stringify(r)).join(", ")}</div>}
                {isNonEmpty(routesClose) && <div className="text-xs text-rose-600">Closed: {routesClose.join(", ")}</div>}
              </div>
            )}

            {/* Staff redundancies */}
            {isNonEmpty(staffRedundancies) && (
              <StaffBlock data={staffRedundancies} label="Staff Redundancies" />
            )}

            {/* Codeshare routes */}
            {isNonEmpty(codeshareRoutes) && (
              <div className="text-xs"><span className="text-slate-500">Codeshare Routes:</span> <span className="font-mono">{codeshareRoutes.join(", ")}</span></div>
            )}

            {/* Event responses */}
            {isNonEmpty(eventResponses) && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Event Responses</p>
                {Object.entries(eventResponses).map(([eid, resp]) => (
                  <div key={eid} className="text-xs flex gap-2"><span className="text-slate-500">{eid}:</span><span className="font-mono">{resp}</span></div>
                ))}
              </div>
            )}

            {/* Uncategorized fields (catch-all) */}
            {uncategorized.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Other</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {uncategorized.map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-slate-500 truncate mr-2">{labelFromKey(k)}</span>
                      <span className="font-mono text-slate-800 shrink-0">
                        {typeof v === "object" ? <ComplexValue value={v} /> : formatValue(v)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, bold, color, small }) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold" : ""}`}>
      <span className={`${small ? "text-xs" : ""} text-slate-500`}>{label}</span>
      <span className={`font-mono ${small ? "text-xs" : ""} ${color || "text-slate-800"} shrink-0`}>{value}</span>
    </div>
  );
}
